import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GitMerge,
  Filter,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  BarChart3,
  History,
  Settings as SettingsIcon
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DuplicateSetCard } from '@/components/duplicates/DuplicateSetCard';
import { MergePanel } from '@/components/duplicates/MergePanel';
import { MergeHistory } from '@/components/duplicates/MergeHistory';
import { DuplicateConfigPanel } from '@/components/duplicates/DuplicateConfigPanel';
import { DuplicateReportPanel } from '@/components/duplicates/DuplicateReportPanel';
import {
  useDuplicateSets,
  useDuplicateSet,
  useMergeDuplicates,
  useUpdateSetStatus
} from '@/hooks/useDuplicates';
import { DuplicateSet, DuplicateSetStatus, MatchType, MergeConfig } from '@/types/duplicates';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type TabType = 'sets' | 'history' | 'reports' | 'settings';

export const DuplicateListPage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('sets');
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [showMergePanel, setShowMergePanel] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<DuplicateSetStatus | ''>('');
  const [matchTypeFilter, setMatchTypeFilter] = useState<MatchType | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Hooks
  const {
    sets,
    pagination,
    isLoading,
    filters,
    updateFilter,
    clearFilters,
    setPage,
    refetch
  } = useDuplicateSets({
    status: statusFilter || undefined,
    matchType: matchTypeFilter || undefined
  });

  const { data: selectedSet } = useDuplicateSet(selectedSetId);
  const mergeDuplicates = useMergeDuplicates();
  const updateSetStatus = useUpdateSetStatus();

  // Handlers
  const handleSetClick = (set: DuplicateSet) => {
    setSelectedSetId(set.id);
    setShowMergePanel(true);
  };

  const handleStatusChange = async (setId: string, status: DuplicateSetStatus) => {
    try {
      await updateSetStatus.mutateAsync({ setId, status });
      toast({
        title: 'Status updated',
        description: `Duplicate set status changed to ${status.toLowerCase()}.`
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        variant: 'destructive'
      });
    }
  };

  const handleMerge = async (config: MergeConfig) => {
    if (!selectedSetId) return;

    try {
      await mergeDuplicates.mutateAsync({ setId: selectedSetId, config });
      toast({
        title: 'Merge successful',
        description: 'Duplicate leads have been merged successfully.'
      });
      setShowMergePanel(false);
      setSelectedSetId(null);
    } catch (err: any) {
      toast({
        title: 'Merge failed',
        description: err.message || 'Failed to merge duplicates.',
        variant: 'destructive'
      });
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setMatchTypeFilter('');
    setSearchQuery('');
    clearFilters();
  };

  // Filter sets by search query
  const filteredSets = sets.filter(set => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const members = Array.isArray(set.members) ? set.members : [];
    return members.some(m =>
      m.lead?.name?.toLowerCase().includes(query) ||
      m.lead?.company?.toLowerCase().includes(query) ||
      (m.lead?.email && m.lead.email.toLowerCase().includes(query))
    );
  });

  const tabs: { id: TabType; label: string; icon: React.ReactNode; roleRequired?: string[] }[] = [
    { id: 'sets', label: 'Duplicate Sets', icon: <GitMerge className="h-4 w-4" /> },
    { id: 'history', label: 'Merge History', icon: <History className="h-4 w-4" /> },
    { id: 'reports', label: 'Reports', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon className="h-4 w-4" />, roleRequired: ['CEO'] }
  ];

  const visibleTabs = tabs.filter(tab =>
    !tab.roleRequired || tab.roleRequired.includes(user?.role || '')
  );

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <GitMerge className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Duplicate Management</h1>
              <p className="text-gray-600">
                Review and merge duplicate leads
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors",
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  )}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'sets' && (
          <>
            {/* Filters */}
            <Card className="p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, company, or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Status filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as DuplicateSetStatus | '');
                    updateFilter('status', e.target.value || undefined);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="REVIEWING">Reviewing</option>
                  <option value="MERGED">Merged</option>
                  <option value="DISMISSED">Dismissed</option>
                  <option value="FLAGGED">Flagged</option>
                </select>

                {/* Match type filter */}
                <select
                  value={matchTypeFilter}
                  onChange={(e) => {
                    setMatchTypeFilter(e.target.value as MatchType | '');
                    updateFilter('matchType', e.target.value || undefined);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Match Types</option>
                  <option value="EXACT_EMAIL">Exact Email</option>
                  <option value="EXACT_PHONE">Exact Phone</option>
                  <option value="FUZZY_NAME_COMPANY">Fuzzy Name/Company</option>
                  <option value="MULTIPLE_FIELDS">Multiple Fields</option>
                </select>

                {/* Clear filters */}
                {(statusFilter || matchTypeFilter || searchQuery) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </Card>

            {/* Sets list */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredSets.length === 0 ? (
              <Card className="p-12">
                <div className="text-center">
                  <GitMerge className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Duplicate Sets Found
                  </h3>
                  <p className="text-gray-600">
                    {searchQuery || statusFilter || matchTypeFilter
                      ? 'Try adjusting your filters to see more results.'
                      : 'No duplicate leads have been detected yet.'}
                  </p>
                </div>
              </Card>
            ) : (
              <>
                <div className="space-y-4">
                  {filteredSets.map(set => (
                    <DuplicateSetCard
                      key={set.id}
                      set={set}
                      onClick={handleSetClick}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <p className="text-sm text-gray-600">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} sets
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'history' && <MergeHistory />}

        {activeTab === 'reports' && <DuplicateReportPanel />}

        {activeTab === 'settings' && <DuplicateConfigPanel />}

        {/* Merge Panel Modal */}
        <AnimatePresence>
          {showMergePanel && selectedSet && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowMergePanel(false);
                setSelectedSetId(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <MergePanel
                  set={selectedSet}
                  onMerge={handleMerge}
                  onCancel={() => {
                    setShowMergePanel(false);
                    setSelectedSetId(null);
                  }}
                  isLoading={mergeDuplicates.isPending}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};
